/* eslint-disable no-unused-vars */
import React from "react";
import ActivateCalendarImage from "./ActivateCalendarImage";
import { LuCalendarDays } from "react-icons/lu";
import Revoke from "./Revoke";
import { useSession, useSupabaseClient } from "@supabase/auth-helpers-react";
import Logo from "./Logo";
import MobileLogo from "./MobileLogo";
import HomeButton from "./HomeButton";
import { Link } from "react-router-dom";

function ScheduleMeetings() {
  const [isCreatingMeetings, setIsCreatingMeetings] = React.useState(false);
  const [accessTokenExpire, setAccessTokenExpire] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [meetings, setMeetings] = React.useState(0);
  const supabase = useSupabaseClient();
  const session = useSession();

  const userEmail = React.useMemo(() => session?.user?.email || "", [session]);

  const getMeetings = React.useCallback(async () => {
    if (!userEmail) return;

    try {
      setIsLoading(true);

      const response = await fetch(
        `https://sbd-availability-api-production-1624.up.railway.app/api/tickets/count/${userEmail}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(`HTTP ${response.status}: ${error.error.message}`);
      }

      const meetingsData = await response.json();
      setMeetings(meetingsData.count || 0);
    } catch (error) {
      console.error("Error fetching meetings:", error);
      setMeetings(0); 
    } finally {
      setIsLoading(false);
    }
  }, [userEmail]);

  React.useEffect(() => {
    if (session) {
      getMeetings();
    }
  }, [session, getMeetings]);

  const createMeetings = async () => {
    try {
      setIsCreatingMeetings(true);

      const response = await fetch(
        "https://sbd-availability-api-production-1624.up.railway.app/api/tickets/create",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            teacherEmail: session?.user?.email,
            accessToken: session?.provider_token,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(`HTTP ${response.status}: ${error.error.message}`);
      }

      setIsCreatingMeetings(false);
      await supabase.auth.signOut();
    } catch (error) {
      console.error("Error creating meetings:", error.message);
      setAccessTokenExpire(true);
      setIsCreatingMeetings(false);
    }
  };

  const logout = async () => {
    await supabase.auth.signOut();
    setAccessTokenExpire(false);
    setIsCreatingMeetings(false);
  };

  return (
    <div className="w-full grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:block h-screen">
        <ActivateCalendarImage />
      </div>
      <main className="flex w-full bg-blue-600 flex-col h-screen">
        <div className="flex items-center justify-center min-h-screen px-4 md:px-6">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
            <div className="flex flex-col items-center justify-center space-y-6">
              <div className="w-60">
                <Logo />
              </div>

              <div className="md:hidden inline-block">
                <MobileLogo />
              </div>
              <div className="bg-green-500 text-white rounded-full p-3">
                <LuCalendarDays className="h-8 w-8" />
              </div>

              <h1
                className={`text-center font-bold ${
                  isCreatingMeetings
                    ? "text-xl md:text-2xl text-gray-600"
                    : "text-2xl md:text-3xl text-gray-900"
                }`}
              >
                {isCreatingMeetings
                  ? "Creating Meeting Links..."
                  : "Generate Meeting Links"}
              </h1>
              <h4 className="text-center text-lg font-medium text-black">
                {isCreatingMeetings
                  ? "Please wait while we create your meetings..."
                  : `You have ${
                      isLoading ? "__" : meetings
                    } meetings pending scheduling completion.`}
              </h4>
              <div className="space-y-2 text-center">
                {meetings === 0 ? (
                  <p className="text-gray-500 dark:text-gray-400 text-justify">
                    Manage your calendar permissions effortlessly. Revoke access
                    whenever you don’t have meetings to schedule. To proceed
                    with scheduling or managing meetings, you’ll need to grant
                    access again.
                  </p>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 text-justify">
                    Easily create and share meeting links to streamline
                    scheduling and save time effortlessly!
                  </p>
                )}
              </div>

              {meetings === 0 ? (
                <button
                  onClick={logout}
                  className="px-4 py-2 w-full rounded transition bg-red-900 hover:bg-gray-900 text-white"
                >
                  Revoke Calendar Access
                </button>
              ) : !accessTokenExpire ? (
                <button
                  onClick={createMeetings}
                  className={`px-4 py-2 w-full rounded transition ${
                    isCreatingMeetings
                      ? "bg-gray-600 hover:bg-gray-900"
                      : "bg-blue-600 hover:bg-blue-900"
                  } text-white`}
                >
                  {isCreatingMeetings
                    ? "Completing Scheduling Classes"
                    : "Complete Scheduling Classes"}
                </button>
              ) : (
                <Revoke />
              )}

              <HomeButton />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default ScheduleMeetings;
